import { Empty, Skeleton } from 'antd';
import type { ArticleRow } from '../../api/client';
import CoverThumb from './CoverThumb';
import StatusBadge from './StatusBadge';
import { fmtTaipei } from './status';

interface Props {
  items: ArticleRow[];
  loading: boolean;
  onEdit: (id: string) => void;
}

/** 手機列表的短日期：今年只顯示「MM/DD HH:mm」，其他年份顯示「YYYY/MM/DD」。 */
function shortDate(t: number): string {
  const s = fmtTaipei(t); // 例：2026/10/16 14:30
  const thisYear = fmtTaipei(Date.now()).slice(0, 4);
  return s.startsWith(thisYear) ? s.slice(5) : s.slice(0, 10);
}

/** 一列的次要資訊：第一行狀態＋日期，第二行分類 · 作者（空的略過）。分兩行才不會換行時行首掛著分隔點。 */
function Meta({ a }: { a: ArticleRow }) {
  const scheduled = a.status === 'scheduled' && a.scheduledAt;
  const byline = [a.categoryNames, a.authorName].filter(Boolean).join(' · ');
  return (
    <div className="mt-1.5 space-y-0.5 text-xs text-gray-500">
      <div className="flex items-center gap-2">
        <StatusBadge status={a.status} />
        <span className="truncate">
          {scheduled ? `排程 ${shortDate(a.scheduledAt!)}` : shortDate(a.updatedAt)}
        </span>
      </div>
      {byline && <div className="truncate">{byline}</div>}
    </div>
  );
}

/** 文章列表（手機）：表格在窄螢幕擠不下，改成一列一張的卡片清單，點整列進編輯。 */
export default function ArticleCardList({ items, loading, onEdit }: Props) {
  if (loading) {
    return (
      <div className="space-y-4 rounded-xl border border-black/[0.06] bg-white p-3">
        {[0, 1, 2, 3, 4].map((i) => (
          <Skeleton key={i} active avatar={{ shape: 'square', size: 60 }} paragraph={{ rows: 1 }} />
        ))}
      </div>
    );
  }
  if (items.length === 0) {
    return <Empty className="py-12" description="找不到符合條件的文章" />;
  }
  return (
    <ul className="m-0 list-none divide-y divide-black/[0.06] overflow-hidden rounded-xl border border-black/[0.06] bg-white p-0">
      {items.map((a) => (
        <li key={a.id}>
          <button
            type="button"
            onClick={() => onEdit(a.id)}
            className="flex min-h-[84px] w-full cursor-pointer gap-3 border-0 bg-transparent p-3 text-left active:bg-gray-50"
          >
            <CoverThumb url={a.coverUrl} sizeClassName="h-[60px] w-20" />
            <div className="min-w-0 flex-1">
              <div className="line-clamp-2 text-[15px] font-medium leading-snug text-gray-800">
                {a.title || '（未命名）'}
              </div>
              <Meta a={a} />
            </div>
          </button>
        </li>
      ))}
    </ul>
  );
}
